/**
 * A focused frame drawn above the wall: the shared window shell bound to the
 * focus presentation. Gestures end in focus commands that write the
 * presentation's boxes; the overlay draws them.
 */
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import {
  beginNativeGlassGesture,
  endNativeGlassGesture,
} from "@/desktop/native-glass-gesture";
import { beginFocusWindowTransfer } from "@/desktop/floating-frame-transfer";
import { selectFrameWindow } from "@/lib/focus-frame-activation";
import type { FocusWindowBox } from "@/lib/focus-window-layout";
import {
  applyFocusedFrameWindowMode,
  focusWindowForFrame,
  isFocusWindowManagedFrame,
  placeFocusedFrameWindow,
  toggleFocusedFrameWindow,
} from "@/lib/focus-window-presentation";
import {
  clampFrameHeaderToSurface,
  draggedNormalBox,
  focusSnapPreviewAt,
  restoredFrameBoxForPointer,
  type FocusSnapPreview,
} from "@/lib/frame-window-gestures";
import type { FocusEnterKind, FocusMotion } from "@/lib/focus/focus-types";
import { useFrame } from "@/stores/frames-selectors";
import { useFramesStore } from "@/stores/frames-store";
import { useSelectionStore } from "@/stores/selection-store";
import { CardGrowBody } from "./card-grow-body";
import {
  FrameWindowShell,
  type FrameWindowDrag,
  type SurfaceSize,
} from "./frame-window-shell";
import { overlayFrameRenderer } from "./overlay-frame-renderer";
import { useCardGrow } from "./use-card-grow";
import "./presentation-enter.css";

/** Entrances drawn by a class; `from-card` grows in `use-card-grow.ts`. */
const MOTION_CLASSES: Record<FocusEnterKind, string> = {
  reveal: "presentation-enter-reveal",
  up: "presentation-enter-up",
  down: "presentation-enter-down",
};

function surfaceBox(surface: SurfaceSize): FocusWindowBox {
  return { x: 0, y: 0, ...surface };
}

/** Header drag: restore a snapped window, preview snaps, then commit. A drag
 *  across the display edge hands the window to the floating layer. */
function focusWindowDrag(
  frameId: string,
  onSnapPreview: (preview: FocusSnapPreview | null) => void,
): FrameWindowDrag {
  return {
    start(pointer, box, press) {
      beginNativeGlassGesture("window-drag");
      const frame = useFramesStore.getState().framesById[frameId];
      if (frame)
        beginFocusWindowTransfer({
          frame,
          box,
          surfaceOrigin: {
            x: press.clientX - pointer.x,
            y: press.clientY - pointer.y,
          },
          press,
        });
      const focusWindow = focusWindowForFrame(frameId);
      return focusWindow && focusWindow.mode !== "normal"
        ? restoredFrameBoxForPointer(pointer, box, focusWindow.normalBox)
        : box;
    },
    move(pointer, box, surface) {
      // Keeps the gesture alive past native glass's quiet timeout.
      beginNativeGlassGesture("window-drag");
      onSnapPreview(
        isFocusWindowManagedFrame(frameId)
          ? focusSnapPreviewAt(pointer, surface)
          : null,
      );
      return clampFrameHeaderToSurface(box, surfaceBox(surface));
    },
    end(pointer, box, surface) {
      endNativeGlassGesture("window-drag");
      onSnapPreview(null);
      const preview = focusSnapPreviewAt(pointer, surface);
      const normalBox = clampFrameHeaderToSurface(
        draggedNormalBox(box, focusWindowForFrame(frameId)),
        surfaceBox(surface),
      );
      if (preview) {
        applyFocusedFrameWindowMode(frameId, preview.mode, {
          normalBox,
          duration: 180,
        });
      } else {
        placeFocusedFrameWindow(frameId, normalBox);
      }
    },
    cancel() {
      endNativeGlassGesture("window-drag");
      onSnapPreview(null);
    },
  };
}

interface FocusWindowProps {
  frameId: string;
  box: FocusWindowBox;
  /** Stacks above the other windows. */
  active: boolean;
  onSnapPreview: (preview: FocusSnapPreview | null) => void;
  /** The live body mounted (a card-only frame: once its detail landed). */
  onBodyMount?: (frameId: string, element: HTMLElement) => void;
  /** How this window enters, when it is the one the presentation moves. */
  motion?: FocusMotion;
  /** The presentation closed: return into the wall card it grew from. */
  returning?: boolean;
  /** Grow progress each frame (0 = the card, 1 = open). */
  onGrowProgress?: (progress: number) => void;
  /** The return into the card settled. */
  onReturned?: () => void;
}

export const FocusWindow = memo(function FocusWindow({
  frameId,
  box,
  active,
  onSnapPreview,
  onBodyMount,
  motion,
  returning = false,
  onGrowProgress,
  onReturned,
}: FocusWindowProps) {
  const frame = useFrame(frameId);
  const selected = useSelectionStore(
    (state) => state.selectedFrameId === frameId,
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const grow = useCardGrow({
    frameId,
    box,
    motion,
    returning,
    rootRef,
    onProgress: onGrowProgress,
    onReturned,
  });
  const drag = useMemo(
    () => focusWindowDrag(frameId, onSnapPreview),
    [frameId, onSnapPreview],
  );
  const activate = useCallback(() => {
    if (!active || useSelectionStore.getState().selectedFrameId !== frameId)
      selectFrameWindow(frameId);
  }, [active, frameId]);
  const toggle = useCallback(() => {
    toggleFocusedFrameWindow(frameId);
  }, [frameId]);
  const resizeEnd = useCallback(
    (resized: FocusWindowBox) => {
      placeFocusedFrameWindow(frameId, resized);
    },
    [frameId],
  );

  // `motion.seq` replays the reveal or up/down entrance on this window.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !motion || motion.kind === "from-card") return;
    root.classList.remove(...Object.values(MOTION_CLASSES));
    void root.offsetWidth;
    root.classList.add(MOTION_CLASSES[motion.kind]);
  }, [motion]);

  const bodyReady = !!frame && frame.contentState !== "card";
  const bodySignalled = useRef(false);
  useEffect(() => {
    if (!bodyReady) {
      bodySignalled.current = false;
      return;
    }
    const root = rootRef.current;
    if (!root || !onBodyMount || bodySignalled.current) return;
    bodySignalled.current = true;
    onBodyMount(frameId, root);
  }, [bodyReady, frameId, onBodyMount]);

  if (!frame) return null;
  return (
    <FrameWindowShell
      testIdPrefix="focus-window"
      frame={frame}
      selected={selected}
      box={box}
      renderer={overlayFrameRenderer}
      drag={drag}
      onActivate={activate}
      onToggle={toggle}
      onResizeEnd={resizeEnd}
      rootRef={rootRef}
      className={active ? "z-10" : undefined}
      wrapBody={(body) => (
        <CardGrowBody frame={frame} flight={grow.flight} layers={grow.layers}>
          {body}
        </CardGrowBody>
      )}
    />
  );
});
